import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Tags as TagsIcon,
  Trash2,
  UsersRound,
} from "lucide-react";
import { WsSegmented } from "src/stage/WsControls";
import { Kpi, PageHeader, Skeleton, StagePage } from "src/stage/StageUi";

import useTags from "./hooks/useTags";
import TagFormModal from "./component/TagFormModal";
import ConfirmationModal from "src/components/Sidebar/ConfirmationModal";
import "./tags.css";
import { getTagIcon } from "src/components/tagIcons";

const TAG_VIEWS = [
  { id: "all", label: "All tags" },
  { id: "used", label: "In use" },
  { id: "unused", label: "Unused" },
];

const safeColor = (color) => (
  /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(color || "") ? color : "#2F8FD4"
);

// AUTO-WA-39: Stage card. The tag's own colour stays; everything else is shared.
function TagCard({ tag, onEdit, onDelete }) {
  const Icon = getTagIcon(tag?.icon) || TagsIcon;
  const color = safeColor(tag?.color);
  const contactCount = Number(tag?.contacts_count) || 0;

  return (
    <article className="ws-card wa-tag">
      <div className="wa-tag-top">
        <span className="wa-tag-icon" style={{ color, background: `${color}14`, boxShadow: `inset 0 0 0 1px ${color}2e` }} aria-hidden="true">
          <Icon size={18} strokeWidth={1.8} />
        </span>
        <div className="ws-grow" style={{ minWidth: 0 }}>
          <h3 className="ws-h3 wa-ellipsis">{tag.name}</h3>
          <span className="ws-muted wa-tag-count"><UsersRound size={12} aria-hidden="true" />{contactCount.toLocaleString("en-IN")} {contactCount === 1 ? "contact" : "contacts"}</span>
        </div>
        <span className="wa-tag-actions">
          <button type="button" className="ws-row-more" onClick={() => onEdit?.(tag)} aria-label={`Edit ${tag.name}`}><Pencil size={14} /></button>
          <button type="button" className="ws-row-more wa-danger-hover" onClick={() => onDelete?.(tag)} aria-label={`Delete ${tag.name}`}><Trash2 size={14} /></button>
        </span>
      </div>
      <p className="wa-tag-desc">{tag.description || "No description yet. Add one so the whole team uses this tag the same way."}</p>
      <span className="wa-tag-pill" aria-hidden="true" style={{ color, background: `${color}14`, boxShadow: `inset 0 0 0 1px ${color}33` }}>
        <Icon size={11} strokeWidth={2} aria-hidden="true" />{tag.name}
      </span>
    </article>
  );
}

function LoadingTags() {
  return (
    <div className="wa-tags-grid">
      {Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} height={150} radius={20} />)}
    </div>
  );
}

function Tags() {
  const {
    tags,
    loading,
    error,
    reload,
    createTag,
    updateTag,
    deleteTag,
  } = useTags();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [query, setQuery] = useState("");
  const [view, setView] = useState("all");

  const totalAssignments = useMemo(
    () => tags.reduce((total, tag) => total + (Number(tag?.contacts_count) || 0), 0),
    [tags],
  );

  const unusedCount = useMemo(
    () => tags.filter((tag) => (Number(tag?.contacts_count) || 0) === 0).length,
    [tags],
  );

  const visibleTags = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return tags.filter((tag) => {
      const count = Number(tag?.contacts_count) || 0;
      const matchesView = view === "all"
        || (view === "used" && count > 0)
        || (view === "unused" && count === 0);
      const matchesQuery = !normalizedQuery
        || String(tag?.name || "").toLowerCase().includes(normalizedQuery)
        || String(tag?.description || "").toLowerCase().includes(normalizedQuery);

      return matchesView && matchesQuery;
    });
  }, [query, tags, view]);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (tag) => {
    setEditing(tag);
    setModalOpen(true);
  };

  return (
    <StagePage className="wa-tags" label="Tags">
      <PageHeader
        eyebrow="Reach"
        title="Tags"
        description="Group contacts so your team shares one language, and so broadcasts reach the right people."
        actions={<button type="button" className="ws-btn ws-btn--primary" onClick={openCreate}><Plus size={16} />New tag</button>}
      />

      {error ? (
        <div className="wa-notice wa-notice--bad" role="alert">
          <AlertTriangle size={16} />
          <span className="ws-grow">{error}</span>
          <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={reload}><RotateCcw size={13} />Try again</button>
        </div>
      ) : null}

      <section className="ws-card ws-kpis" aria-label="Tag overview">
        <Kpi icon={TagsIcon} label="Tags" loading={loading} value={tags.length.toLocaleString("en-IN")} />
        <Kpi icon={UsersRound} label="Tag assignments" loading={loading} value={totalAssignments.toLocaleString("en-IN")} />
        <Kpi label="In use" loading={loading} value={(tags.length - unusedCount).toLocaleString("en-IN")} />
        <Kpi label="Unused" loading={loading} value={unusedCount.toLocaleString("en-IN")} hint={unusedCount ? "Remove or reuse them to keep things tidy" : null} />
      </section>

      <div className="ws-controls">
        <WsSegmented
          label="Show tags"
          items={TAG_VIEWS.map((item) => ({ ...item, count: item.id === "all" ? tags.length : item.id === "used" ? tags.length - unusedCount : unusedCount }))}
          value={view}
          onChange={setView}
        />
        <div className="ws-controls-end">
          <label className="ws-searchbox">
            <Search size={15} aria-hidden="true" />
            <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search tags" aria-label="Search tags" />
          </label>
        </div>
      </div>

      {loading ? (
        <LoadingTags />
      ) : visibleTags.length === 0 ? (
        <div className="ws-card ws-empty wa-tags-empty">
          <span className="ws-icon-tile" aria-hidden="true"><TagsIcon size={20} /></span>
          <strong>{tags.length === 0 ? "Create your first tag" : "No matching tags"}</strong>
          {tags.length === 0
            ? "Start with a tag your team will use often: VIP, New lead, Wholesale, or anything that fits your business."
            : "Try another search or filter."}
          {tags.length === 0 ? <button type="button" className="ws-btn ws-btn--primary ws-btn--sm" style={{ marginTop: 8 }} onClick={openCreate}><Plus size={14} />Create tag</button> : null}
        </div>
      ) : (
        <div className="wa-tags-grid">
          {visibleTags.map((tag) => (
            <TagCard key={tag.id} tag={tag} onEdit={openEdit} onDelete={setDeleting} />
          ))}
        </div>
      )}

      <TagFormModal
        open={modalOpen}
        tag={editing}
        onClose={() => setModalOpen(false)}
        onSubmit={(payload) => (
          editing ? updateTag(editing.id, payload) : createTag(payload)
        )}
      />

      <ConfirmationModal
        open={Boolean(deleting)}
        title="Delete tag?"
        description={deleting
          ? `"${deleting.name}" will be removed from ${deleting.contacts_count ?? 0} contact(s). This can't be undone.`
          : ""}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        icon={Trash2}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          try {
            await deleteTag(deleting.id);
          } finally {
            setDeleting(null);
          }
        }}
      />
    </StagePage>
  );
}

export default Tags;
